import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, Link2, Unlink, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

export default function GoogleCalendarPanel() {
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/oauth/google/status");
      setStatus(r.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Se torniamo dal redirect OAuth gestiamo il param ?gcal=ok|error
    const params = new URLSearchParams(window.location.search);
    const gcal = params.get("gcal");
    if (gcal) {
      if (gcal === "ok") toast.success("Google Calendar collegato");
      else toast.error("Connessione Google Calendar fallita: " + gcal);
      params.delete("gcal");
      const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, [refresh]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/oauth/google/authorize");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare la connessione");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar?")) return;
    setBusy(true);
    try {
      await api.delete("/oauth/google/disconnect");
      toast.success("Google Calendar scollegato");
      await refresh();
    } catch {
      toast.error("Errore");
    } finally {
      setBusy(false);
    }
  };

  const syncShifts = async () => {
    setBusy(true);
    try {
      const r = await api.post("/google/calendar/sync-shifts");
      toast.success(`Sincronizzati ${r.data.shifts_total} turni · creati ${r.data.created} · aggiornati ${r.data.updated}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore di sincronizzazione");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-black bg-white p-6 flex items-center gap-3" data-testid="gcal-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm text-[#555]">Verifico stato Google Calendar…</span>
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="border border-black bg-[#FFF7ED] p-6" data-testid="gcal-not-configured">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#3B82F6] mt-0.5" />
          <div>
            <div className="font-display text-lg">Google Calendar non disponibile</div>
            <p className="text-sm text-[#555] mt-1">
              L'integrazione non è ancora stata configurata dall'amministratore. Contatta il responsabile per attivarla.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-black bg-white p-6" data-testid="gcal-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-1">Sync esterno</div>
          <div className="font-display text-2xl">Google Calendar</div>
          <p className="text-sm text-[#555] mt-1 max-w-md">
            Collega il tuo account Google per sincronizzare turni, ferie e riunioni con Google Calendar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status.connected ? (
            <div className="inline-flex items-center gap-2 text-sm text-[#0A7A3D] bg-[#E8F8EE] border border-[#0A7A3D]/30 px-3 py-2">
              <CheckCircle2 className="w-4 h-4" />
              <span data-testid="gcal-connected-email">{status.google_email}</span>
            </div>
          ) : (
            <div className="text-xs uppercase tracking-widest text-[#757575]">Non connesso</div>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {status.connected ? (
          <>
            <Button
              onClick={syncShifts}
              disabled={busy}
              data-testid="gcal-sync-shifts-btn"
              className="rounded-none bg-[#3B82F6] hover:bg-black h-11"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />
              Sincronizza turni
            </Button>
            <Button
              onClick={disconnect}
              disabled={busy}
              variant="outline"
              data-testid="gcal-disconnect-btn"
              className="rounded-none h-11 border-black"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Scollega
            </Button>
          </>
        ) : (
          <Button
            onClick={connect}
            disabled={busy}
            data-testid="gcal-connect-btn"
            className="rounded-none bg-black text-white hover:bg-[#3B82F6] h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Collega Google Calendar
          </Button>
        )}
      </div>
    </div>
  );
}
